/**
 * Reels Carousel
 * - Entry grid: horizontal tile carousel with autoplay preview videos.
 * - Viewer: fullscreen, one product per slide (video-first, falls back to image).
 *   Desktop  -> horizontal navigation (arrow buttons, drag, ArrowLeft/Right).
 *   Mobile   -> vertical navigation (swipe up/down, ArrowUp/Down), like TikTok.
 *
 * Design choice: only ONE video element is ever mounted/playing at a time.
 * There is no IntersectionObserver juggling many videos inside the viewer and
 * no auto-advancing progress timer — navigation is always a direct, explicit
 * user action (swipe, drag, arrow, key). This is what removes the flicker /
 * black-frame glitches from the previous "stories" implementation.
 */
(() => {
  'use strict';

  window.ReelsCarouselBuild = 'v2.9-whole-price-2026-10-08'; // bump this string on every future edit to confirm the browser loaded the new file (check in DevTools console)

  const MOBILE_QUERY = '(max-width: 749px)';
  const SWIPE_THRESHOLD = 45;
  const TAP_THRESHOLD = 10;

  class ReelsCarousel {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productsPerPage = parseInt(root.dataset.productsPerPage, 10) || 10;
      this.autoplayTiles = root.dataset.autoplayTiles === 'true';

      this.track = root.querySelector('[data-reels-track]');
      this.prevTileBtn = root.querySelector('[data-reels-prev]');
      this.nextTileBtn = root.querySelector('[data-reels-next]');

      this.viewer = root.querySelector('[data-reels-viewer]');
      this.stage = root.querySelector('[data-reels-stage]');
      this.mediaHost = root.querySelector('[data-viewer-media]');
      this.counterEl = root.querySelector('[data-viewer-counter]');
      this.muteBtn = root.querySelector('[data-viewer-mute]');
      this.closeBtn = root.querySelector('[data-viewer-close]');
      this.prevBtn = root.querySelector('[data-viewer-prev]');
      this.nextBtn = root.querySelector('[data-viewer-next]');
      this.likeBtn = root.querySelector('[data-viewer-like]');
      this.shareBtn = root.querySelector('[data-viewer-share]');
      this.addBtn = root.querySelector('[data-viewer-add-to-cart]');
      this.addLabel = root.querySelector('[data-viewer-add-label]');
      this.cardImage = root.querySelector('[data-viewer-card-image]');
      this.cardTitle = root.querySelector('[data-viewer-card-title]');
      this.cardDescription = root.querySelector('[data-viewer-card-description]');
      this.cardPrice = root.querySelector('[data-viewer-card-price]');
      this.variantsWrap = root.querySelector('[data-viewer-variants]');
      this.variantSelect = root.querySelector('[data-viewer-variant-select]');
      this.announcer = root.querySelector('[data-viewer-announcer]');
      this.peekPrev = root.querySelector('[data-viewer-peek-prev]');
      this.peekNext = root.querySelector('[data-viewer-peek-next]');
      this.peekPrevTitle = root.querySelector('[data-viewer-peek-prev-title]');
      this.peekNextTitle = root.querySelector('[data-viewer-peek-next-title]');

      this._variantCache = new Map(); // product handle -> Promise<variants[]>

      this.blocks = new Map(); // blockId -> { data:{page,pages,products[]}, category, title, isLoading }
      this._parseInitialData();

      this.state = {
        blockId: null,
        index: 0,
        muted: true,
        variantAvailable: true,
      };

      this._bindTileCarousel();
      this._bindTileVideos();
      this._bindViewerControls();
      this._bindSwipe();
      this._bindKeyboard();

      let resizeTimer = null;
      window.addEventListener('resize', () => {
        if (!this.state.blockId) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const block = this._currentBlock();
          if (block) this._renderPeeks(block);
        }, 120);
      });
    }

    /* ---------------------------------------------------------------- */
    /* Data                                                              */
    /* ---------------------------------------------------------------- */

    _parseInitialData() {
      this.root.querySelectorAll('[data-reel-data]').forEach((script) => {
        const blockId = script.dataset.blockId;
        let parsed;
        try {
          parsed = this._mapProductLinks(JSON.parse(script.textContent));
        } catch (err) {
          parsed = { page: 1, pages: 1, products: [] };
        }
        this.blocks.set(blockId, {
          data: parsed,
          category: script.dataset.category || '',
          title: script.dataset.reelTitle || '',
          isLoading: false,
        });
      });
    }

    // Each page supplies the explicit reel-product -> actual-product mapping.
    _mapProductLinks(data) {
      const links = data.actualProductUrls || {};
      data.products = (data.products || []).map((product) => ({
        ...product,
        actualProductUrl: links[String(product.id)] || product.actualProductUrl || '',
      }));
      return data;
    }

    _hasMore(block) {
      return block.data.page < block.data.pages;
    }

    async _fetchNextPage(blockId) {
      const block = this.blocks.get(blockId);
      if (!block || block.isLoading || !this._hasMore(block)) return;
      block.isLoading = true;

      const nextPage = block.data.page + 1;
      const url = `${window.location.pathname}?section_id=${this.sectionId}&page=${nextPage}`;

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load more products');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector(`[data-reel-data][data-block-id="${blockId}"]`);
        if (!script) return;

        const parsed = this._mapProductLinks(JSON.parse(script.textContent));
        const existingIds = new Set(block.data.products.map((p) => p.id));
        const fresh = parsed.products.filter((p) => !existingIds.has(p.id));

        block.data.products = block.data.products.concat(fresh);
        block.data.page = parsed.page;
        block.data.pages = parsed.pages;

        if (this.state.blockId === blockId) this._refreshNavState();
      } catch (err) {
        /* Non-critical: viewer simply stays capped at what's already loaded. */
      } finally {
        block.isLoading = false;
      }
    }

    _maybeLoadMore(blockId) {
      const block = this.blocks.get(blockId);
      if (!block) return;
      if (this.state.index >= block.data.products.length - 3) {
        this._fetchNextPage(blockId);
      }
    }

    /* ---------------------------------------------------------------- */
    /* Entry tile carousel (horizontal scroller + drag)                  */
    /* ---------------------------------------------------------------- */

    _bindTileCarousel() {
      if (!this.track) return;

      this.prevTileBtn?.addEventListener('click', () => {
        this.track.scrollBy({ left: -this.track.clientWidth * 0.8, behavior: 'smooth' });
      });
      this.nextTileBtn?.addEventListener('click', () => {
        this.track.scrollBy({ left: this.track.clientWidth * 0.8, behavior: 'smooth' });
      });

      // Mouse drag-to-scroll. Pointer capture only begins once real movement
      // is detected, so a plain click still reaches the tile's button.
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let captured = false;

      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        dragging = true;
        captured = false;
        startX = e.clientX;
        startScroll = this.track.scrollLeft;
      });

      this.track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const delta = e.clientX - startX;
        if (!captured && Math.abs(delta) > 5) {
          captured = true;
          this.track.setPointerCapture(e.pointerId);
        }
        if (captured) this.track.scrollLeft = startScroll - delta;
      });

      const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        if (captured && this.track.hasPointerCapture?.(e.pointerId)) {
          this.track.releasePointerCapture(e.pointerId);
        }
        captured = false;
      };
      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
      this.track.addEventListener('pointerleave', endDrag);

      // Open viewer on tile click.
      this.track.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-reel-open]');
        if (!tile) return;
        this.openViewer(tile.dataset.blockId);
      });
    }

    _bindTileVideos() {
      if (!this.autoplayTiles) return;
      const videos = this.root.querySelectorAll('[data-reel-tile-video]');
      if (!videos.length || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              this._activateTileVideo(video);
              video.play().catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: '80px', threshold: 0.4 }
      );

      videos.forEach((video) => observer.observe(video));
    }

    _activateTileVideo(video) {
      if (video.dataset.initialized) return;
      video.dataset.initialized = 'true';
      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach((source) => {
        source.src = source.dataset.src;
      });
      video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });
      video.load();
    }

    /* ---------------------------------------------------------------- */
    /* Viewer lifecycle                                                   */
    /* ---------------------------------------------------------------- */

    openViewer(blockId) {
      const block = this.blocks.get(blockId);
      if (!block) return;

      this.state.blockId = blockId;
      this.state.index = 0;

      if (typeof this.viewer.showModal === 'function') {
        this.viewer.showModal();
      } else {
        this.viewer.setAttribute('open', '');
      }
      document.documentElement.style.overflow = 'hidden';

      this._renderSlide();
      this._maybeLoadMore(blockId);

      // The dialog's own layout (via showModal) isn't always fully committed
      // in the same tick it opens, which can make the peek panels read a
      // stale/zero width on this very first render. Re-apply them one frame
      // later once layout has settled — the main slide above already
      // rendered instantly, so there's no visible delay to the video/card.
      requestAnimationFrame(() => {
        const current = this._currentBlock();
        if (current) this._renderPeeks(current);
      });
    }

    closeViewer() {
      this._clearMedia();
      if (typeof this.viewer.close === 'function' && this.viewer.open) {
        this.viewer.close();
      } else {
        this.viewer.removeAttribute('open');
      }
      document.documentElement.style.overflow = '';
      this.state.blockId = null;
    }

    _isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    _currentBlock() {
      return this.state.blockId ? this.blocks.get(this.state.blockId) : null;
    }

    _currentProduct() {
      const block = this._currentBlock();
      if (!block) return null;
      return block.data.products[this.state.index] || null;
    }

    /* ---------------------------------------------------------------- */
    /* Slide rendering                                                    */
    /* ---------------------------------------------------------------- */

    _clearMedia() {
      this.mediaHost.querySelectorAll('video').forEach((v) => {
        v.pause();
        v.removeAttribute('src');
        v.load();
      });
      this.mediaHost.innerHTML = '';
    }

    _renderSlide() {
      const block = this._currentBlock();
      const product = this._currentProduct();
      if (!block || !product) return;

      const outgoing = this.mediaHost.querySelector('.is-active');
      if (outgoing) {
        outgoing.classList.remove('is-active');
        if (outgoing.tagName === 'VIDEO') outgoing.pause();
        setTimeout(() => outgoing.remove(), 240);
      }

      let el;
      if (product.video && product.video.sources && product.video.sources.length) {
        el = document.createElement('video');
        el.muted = this.state.muted;
        el.loop = true;
        el.playsInline = true;
        el.setAttribute('playsinline', '');
        el.preload = 'auto';
        el.poster = product.poster || '';
        el.setAttribute('aria-label', product.video.alt || product.title);

        const best = product.video.sources.reduce((a, b) => ((b.width || 0) > (a.width || 0) ? b : a));
        const source = document.createElement('source');
        source.src = best.src;
        source.type = best.mimeType || 'video/mp4';
        el.appendChild(source);
      } else {
        el = document.createElement('img');
        el.src = product.poster || '';
        el.alt = product.posterAlt || product.title;
        el.loading = 'eager';
      }

      this.mediaHost.appendChild(el);
      requestAnimationFrame(() => {
        el.classList.add('is-active');
        if (el.tagName === 'VIDEO') el.play().catch(() => {});
      });

      this._renderCard(product);
      this._updateMuteIcon();
      this._refreshNavState();
      this._renderPeeks(block);
      this._announce(`${product.title}, ${this._formatMoney(product.price)}`);
    }

    _renderPeeks(block) {
      const products = block.data.products;
      const prevProduct = products[this.state.index - 1];
      const nextProduct = products[this.state.index + 1];

      if (this.peekPrev) {
        if (prevProduct) {
          this.peekPrev.style.backgroundImage = `url("${prevProduct.poster}")`;
          if (this.peekPrevTitle) this.peekPrevTitle.textContent = prevProduct.title;
          this.peekPrev.classList.add('is-visible');
          this.peekPrev.setAttribute('tabindex', '0');
        } else {
          this.peekPrev.classList.remove('is-visible');
          this.peekPrev.setAttribute('tabindex', '-1');
        }
      }

      if (this.peekNext) {
        if (nextProduct) {
          this.peekNext.style.backgroundImage = `url("${nextProduct.poster}")`;
          if (this.peekNextTitle) this.peekNextTitle.textContent = nextProduct.title;
          this.peekNext.classList.add('is-visible');
          this.peekNext.setAttribute('tabindex', '0');
        } else {
          this.peekNext.classList.remove('is-visible');
          this.peekNext.setAttribute('tabindex', '-1');
        }
      }
    }

    _renderCard(product) {
      // Always link to the real product page (images + description),
      // never to a reel-specific URL.
      const productUrl = this._productUrl(product);

      this.cardImage.href = productUrl;
      this.cardImage.style.backgroundImage = product.poster ? `url("${product.poster}")` : 'none';

      this.cardTitle.href = productUrl;
      this.cardTitle.textContent = product.title;

      this.cardDescription.textContent = product.description || '';

      this._renderPrice(product.price, product.onSale ? product.compareAtPrice : 0);

      this.addBtn.dataset.variantId = product.variantId;
      this.state.variantAvailable = !!product.available;
      this.addBtn.disabled = !product.available;
      this.addBtn.classList.remove('is-loading', 'is-success');
      this.addLabel.textContent = product.available ? 'Add to cart' : 'Sold out';

      this._renderVariants(product);
    }

    _renderPrice(price, compareAt) {
      this.cardPrice.innerHTML = '';
      const priceEl = document.createElement('span');
      priceEl.textContent = this._formatMoney(price);
      this.cardPrice.appendChild(priceEl);
      if (compareAt && compareAt > price) {
        const compareEl = document.createElement('span');
        compareEl.className = 'compare';
        compareEl.textContent = this._formatMoney(compareAt);
        this.cardPrice.appendChild(compareEl);
      }
    }

    /* ---------------------------------------------------------------- */
    /* Variants                                                           */
    /* ---------------------------------------------------------------- */

    _productHandle(product) {
      if (product.handle) return product.handle;
      try {
        const m = new URL(product.url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
        return m ? m[1] : '';
      } catch (err) {
        return '';
      }
    }

    _fetchVariants(handle) {
      if (!this._variantCache.has(handle)) {
        const root = window.Shopify?.routes?.root || '/';
        const request = fetch(`${root}products/${handle}.js`)
          .then((res) => {
            if (!res.ok) throw new Error('Variants unavailable');
            return res.json();
          })
          .then((data) => data.variants || [])
          .catch((err) => {
            this._variantCache.delete(handle);
            throw err;
          });
        this._variantCache.set(handle, request);
      }
      return this._variantCache.get(handle);
    }

    async _renderVariants(product) {
      const wrap = this.variantsWrap;
      const select = this.variantSelect;
      if (!wrap || !select) return;
      wrap.hidden = true;
      select.innerHTML = '';
      select.onchange = null;

      const handle = this._productHandle(product);
      if (!handle) return;

      let variants;
      try {
        variants = await this._fetchVariants(handle);
      } catch (err) {
        return; // Falls back to the default variant.
      }
      // The visitor may have moved to another slide while loading.
      if (this._currentProduct() !== product) return;
      if (!variants || variants.length < 2) return;

      variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.available ? variant.title : `${variant.title} — Sold out`;
        select.appendChild(option);
      });

      const current = variants.find((v) => String(v.id) === String(product.variantId));
      const initial = current || variants.find((v) => v.available) || variants[0];
      select.value = String(initial.id);
      this._applyVariant(initial);
      wrap.hidden = false;

      select.onchange = () => {
        const chosen = variants.find((v) => String(v.id) === select.value);
        if (chosen) this._applyVariant(chosen);
      };
    }

    _applyVariant(variant) {
      this.addBtn.dataset.variantId = String(variant.id);
      this.state.variantAvailable = !!variant.available;
      this.addBtn.disabled = !variant.available;
      this.addBtn.classList.remove('is-success', 'is-loading');
      this.addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
      this._renderPrice(variant.price, variant.compare_at_price);
    }

    _refreshNavState() {
      const block = this._currentBlock();
      if (!block) return;
      const total = block.data.total || block.data.products.length;
      this.counterEl.textContent = `${this.state.index + 1} / ${total}`;
      if (this.prevBtn) this.prevBtn.disabled = this.state.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.state.index >= total - 1 && !this._hasMore(block);
    }

    _updateMuteIcon() {
      const onIcon = this.root.querySelector('[data-viewer-mute-on]');
      const offIcon = this.root.querySelector('[data-viewer-mute-off]');
      if (onIcon) onIcon.style.display = this.state.muted ? 'none' : '';
      if (offIcon) offIcon.style.display = this.state.muted ? '' : 'none';
      this.muteBtn?.setAttribute('aria-label', this.state.muted ? 'Unmute' : 'Mute');
    }

    /* ---------------------------------------------------------------- */
    /* Navigation                                                         */
    /* ---------------------------------------------------------------- */

    next() {
      const block = this._currentBlock();
      if (!block) return;
      if (this.state.index < block.data.products.length - 1) {
        this.state.index += 1;
        this._renderSlide();
        this._maybeLoadMore(this.state.blockId);
      }
    }

    prev() {
      if (this.state.index > 0) {
        this.state.index -= 1;
        this._renderSlide();
      }
    }

    togglePlayPause() {
      const video = this.mediaHost.querySelector('video.is-active');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    /* ---------------------------------------------------------------- */
    /* Controls                                                           */
    /* ---------------------------------------------------------------- */

    _bindViewerControls() {
      this.closeBtn?.addEventListener('click', () => this.closeViewer());
      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());
      this.peekPrev?.addEventListener('click', () => this.prev());
      this.peekNext?.addEventListener('click', () => this.next());

      this.viewer.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeViewer();
      });
      this.viewer.addEventListener('click', (e) => {
        if (e.target === this.viewer) this.closeViewer();
      });

      this.muteBtn?.addEventListener('click', () => {
        this.state.muted = !this.state.muted;
        const video = this.mediaHost.querySelector('video.is-active');
        if (video) video.muted = this.state.muted;
        this._updateMuteIcon();
      });

      this.likeBtn?.addEventListener('click', () => {
        const pressed = this.likeBtn.getAttribute('aria-pressed') === 'true';
        this.likeBtn.setAttribute('aria-pressed', String(!pressed));
        const product = this._currentProduct();
        this.root.dispatchEvent(
          new CustomEvent('reel:wishlist-toggle', { bubbles: true, detail: { product, saved: !pressed } })
        );
      });

      this.shareBtn?.addEventListener('click', async () => {
        const product = this._currentProduct();
        if (!product) return;
        const shareUrl = new URL(this._productUrl(product), window.location.origin).href;
        if (navigator.share) {
          navigator.share({ title: product.title, url: shareUrl }).catch(() => {});
        } else if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(shareUrl);
            this._announce('Link copied to clipboard');
          } catch (err) {
            /* Clipboard not available; ignore silently. */
          }
        }
      });

      this.addBtn?.addEventListener('click', () => this._addToCart(this.addBtn));
    }

    _bindKeyboard() {
      this.viewer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeViewer();
          return;
        }
        // Let the variant dropdown use its own arrow/space keys.
        if (e.target.closest && e.target.closest('select')) return;
        const mobile = this._isMobile();
        if ((mobile && e.key === 'ArrowUp') || (!mobile && e.key === 'ArrowLeft')) {
          e.preventDefault();
          this.prev();
        } else if ((mobile && e.key === 'ArrowDown') || (!mobile && e.key === 'ArrowRight')) {
          e.preventDefault();
          this.next();
        } else if (e.key === ' ') {
          e.preventDefault();
          this.togglePlayPause();
        }
      });
    }

    /* ---------------------------------------------------------------- */
    /* Swipe / tap                                                        */
    /* ---------------------------------------------------------------- */

    _bindSwipe() {
      let tracking = false;
      let startX = 0;
      let startY = 0;
      let startTime = 0;

      this.stage.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button, a')) return;
        tracking = true;
        startX = e.clientX;
        startY = e.clientY;
        startTime = Date.now();
      });

      this.stage.addEventListener('pointerup', (e) => {
        if (!tracking) return;
        tracking = false;

        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        const elapsed = Date.now() - startTime;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (absX < TAP_THRESHOLD && absY < TAP_THRESHOLD && elapsed < 400) {
          this.togglePlayPause();
          return;
        }

        const mobile = this._isMobile();
        if (mobile) {
          if (absY > SWIPE_THRESHOLD && absY > absX) {
            if (deltaY < 0) this.next();
            else this.prev();
          }
        } else if (absX > SWIPE_THRESHOLD && absX > absY) {
          if (deltaX < 0) this.next();
          else this.prev();
        }
      });

      this.stage.addEventListener('pointercancel', () => {
        tracking = false;
      });

      // Belt-and-suspenders: some mobile browsers still try to hand a touch
      // gesture to native scroll/zoom handling even with touch-action set in
      // CSS (this is what caused "only works with two fingers" — the
      // system was treating a single-finger touch as a page pan). Blocking
      // touchmove's default directly guarantees the gesture stays ours,
      // regardless of finger count.
      this.stage.addEventListener(
        'touchmove',
        (e) => {
          if (e.target.closest('button, a')) return;
          if (e.cancelable) e.preventDefault();
        },
        { passive: false }
      );
    }

    /* ---------------------------------------------------------------- */
    /* Cart                                                               */
    /* ---------------------------------------------------------------- */

    async _addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled || this._cartAdding) return;
      this._cartAdding = true;
      button.classList.add('is-loading');
      button.disabled = true;
      this.addLabel.textContent = 'Adding…';
      const root = window.Shopify?.routes?.root || '/';
      // Only cart UI fragments are replaced, leaving the reel and header intact.
      const selectors = ['cart-items-component', '#CartDrawer .drawer__inner',
        '#cart-icon-bubble', 'cart-icon', '#main-cart-items', '#main-cart-footer'];
      const targets = selectors.flatMap((selector) =>
        Array.from(document.querySelectorAll(selector)).map((element, index) => ({selector, element, index}))
      ).filter((target, index, all) => !all.some((other, otherIndex) =>
        otherIndex !== index && other.element !== target.element && other.element.contains(target.element)));
      const sectionIds = [...new Set(targets.map(({element}) =>
        element.dataset.sectionId || element.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')
      ).filter(Boolean))].slice(0, 5);
      let added = false;
      try {
        const payload = {items: [{id: variantId, quantity: 1}]};
        if (sectionIds.length) {
          payload.sections = sectionIds;
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add to cart');
        added = true;
        let synced = false;
        try {
          const documents = Object.values(data.sections || {}).filter(Boolean)
            .map((html) => new DOMParser().parseFromString(html, 'text/html'));
          // Some themes render the cart outside a section; fetch current markup
          // in the background in that case. This never reloads the browser page.
          if (targets.some(({selector, index}) => !documents.some(doc => doc.querySelectorAll(selector)[index]))) {
            const url = new URL(window.location.href);
            url.searchParams.set('_reels_cart_refresh', Date.now().toString());
            const page = await fetch(url.href, {cache: 'no-store'});
            if (!page.ok) throw new Error('Cart display refresh failed');
            documents.unshift(new DOMParser().parseFromString(await page.text(), 'text/html'));
          }
          let updated = 0;
          targets.forEach(({selector, element, index}) => {
            const fresh = documents.map(doc => doc.querySelectorAll(selector)[index]).find(Boolean);
            if (!fresh || !element.isConnected) return;
            // Script nodes are not executed from returned HTML.
            fresh.querySelectorAll('script').forEach(script => script.remove());
            element.replaceWith(fresh);
            updated += 1;
          });
          synced = targets.length > 0 && updated === targets.length;
          if (updated) document.querySelectorAll('cart-drawer, cart-drawer-items').forEach(el => el.classList.remove('is-empty'));
          document.dispatchEvent(new CustomEvent('cart:updated', {detail: data, bubbles: true}));
        } catch (refreshError) {
          console.warn('Item added, but cart display could not refresh.', refreshError);
        }
        if (button.dataset.variantId === variantId) {
          button.classList.add('is-success');
          this.addLabel.textContent = synced ? 'Added' : 'Added — view cart';
        }
        this._announce(synced ? 'Added to cart. Cart updated.' : 'Item added to cart. Open your cart to view it.');
      } catch (error) {
        if (button.dataset.variantId === variantId) this.addLabel.textContent = 'Try again';
        this._announce(error.message || 'Could not add to cart. Please try again.');
      } finally {
        this._cartAdding = false;
        button.classList.remove('is-loading');
        if (button.dataset.variantId === variantId) {
          // Respect the currently selected variant's availability.
          button.disabled = !this.state.variantAvailable;
          if (added) setTimeout(() => {
            if (button.dataset.variantId !== variantId || this._cartAdding) return;
            button.classList.remove('is-success');
            this.addLabel.textContent = this.state.variantAvailable ? 'Add to cart' : 'Sold out';
          }, 1800);
        }
      }
    }

    /* ---------------------------------------------------------------- */
    /* Utilities                                                          */
    /* ---------------------------------------------------------------- */

    /**
     * Returns the canonical product page URL (the normal page with images
     * and description), stripping any reel-specific query string, hash, or
     * /collections/... prefix that may have been added by the snippet.
     */
    _productUrl(product) {
      // Use the referenced product before considering the reel product handle.
      if (product.actualProductUrl) return product.actualProductUrl;

      const root = window.Shopify?.routes?.root || '/';
      // Prefer the handle if the snippet provides it
      if (product.handle) return `${root}products/${product.handle}`;

      // Otherwise clean up whatever url we were given
      try {
        const u = new URL(product.url, window.location.origin);
        const match = u.pathname.match(/\/products\/[^/]+/);
        return match ? `${root.replace(/\/$/, '')}${match[0]}` : u.pathname;
      } catch (err) {
        return product.url;
      }
    }

    // Uses the store's own money format (Settings > Store details > Currency
    // formatting), so the symbol (e.g. Rs.) always matches your Shopify setup.
    _formatMoney(cents) {
      const format = this.root.dataset.moneyFormat || '{{amount}}';
      const value = Number(cents) / 100;

      const withSeparators = (decimals, thousands, decimal) => {
        // Whole-number prices (e.g. 4813.00) are shown without decimals.
        if (Number.isInteger(value)) decimals = 0;
        const [whole, fraction] = Math.abs(value).toFixed(decimals).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return (value < 0 ? '-' : '') + grouped + (fraction ? decimal + fraction : '');
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
        switch (name) {
          case 'amount':
            return withSeparators(2, ',', '.');
          case 'amount_no_decimals':
            return withSeparators(0, ',', '.');
          case 'amount_with_comma_separator':
            return withSeparators(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withSeparators(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withSeparators(2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return withSeparators(0, ' ', '.');
          case 'amount_with_space_separator':
            return withSeparators(2, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return withSeparators(2, ' ', '.');
          default:
            return withSeparators(2, ',', '.');
        }
      });
    }

    _announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = '';
      requestAnimationFrame(() => {
        this.announcer.textContent = message;
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-reels-carousel]').forEach((root) => {
      if (root.dataset.reelsInitialized) return;
      root.dataset.reelsInitialized = 'true';
      new ReelsCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector?.('[data-reels-carousel]');
    if (root && !root.dataset.reelsInitialized) {
      root.dataset.reelsInitialized = 'true';
      new ReelsCarousel(root);
    }
  });
})();